<template>
    <div class="pls-root">
        <div class="pls-grid-bg"></div>
        <div class="pls-blobs">
            <div class="pls-blob pls-blob--blue"></div>
            <div class="pls-blob pls-blob--purple"></div>
        </div>

        <div class="pls-wrap">
            <!-- Header -->
            <div class="pls-hdr anim-up" style="--d:0ms">
                <div class="pls-hdr-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="8" y="1" width="8" height="5" rx="1.5"/>
                        <rect x="1" y="18" width="8" height="5" rx="1.5"/>
                        <rect x="15" y="18" width="8" height="5" rx="1.5"/>
                        <path d="M12 6v5M12 11L5 18M12 11l7 7"/>
                    </svg>
                </div>
                <h1 class="pls-hdr-title">Binary Placement Setup</h1>
                <p class="pls-hdr-sub">Complete your binary MLM placement to access your account and start earning</p>
            </div>

            <!-- Alert -->
            <div v-if="alert.show" class="pls-alert anim-up" :class="'pls-alert--' + alert.type" style="--d:40ms">
                <span class="pls-alert-icon">
                    <svg v-if="alert.type === 'success'" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
                    <svg v-else-if="alert.type === 'error'" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                    <svg v-else-if="alert.type === 'warning'" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                    <svg v-else viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
                </span>
                <p class="pls-alert-msg">{{ alert.message }}</p>
                <button class="pls-alert-close" @click="alert.show = false">
                    <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
                </button>
            </div>

            <!-- Main Card -->
            <div class="pls-card anim-up" style="--d:90ms">

                <!-- ── Branch A: referrer already known ── -->
                <template v-if="user.has_referrer && referrer">
                    <div class="pls-sec-hdr">
                        <div class="pls-sec-icon pls-sec-icon--green">
                            <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                        </div>
                        <div>
                            <h3 class="pls-sec-title">Referrer Found</h3>
                            <p class="pls-sec-sub">Choose your position under your sponsor</p>
                        </div>
                    </div>

                    <!-- Referrer card -->
                    <div class="pls-ref-card">
                        <div class="pls-ref-head">
                            <div class="pls-avatar">{{ (referrer.display_name || referrer.name)?.charAt(0).toUpperCase() || '?' }}</div>
                            <div class="pls-ref-info">
                                <p class="pls-ref-name">{{ referrer.display_name || referrer.name }}</p>
                                <p class="pls-ref-email">{{ referrer.email }}</p>
                            </div>
                        </div>
                        <div class="pls-meta-grid">
                            <div class="pls-meta-item">
                                <span class="pls-meta-lbl">User ID</span>
                                <span class="pls-meta-val">{{ referrer.uid }}</span>
                            </div>
                            <div class="pls-meta-item">
                                <span class="pls-meta-lbl">Referral Code</span>
                                <span class="pls-meta-val pls-meta-val--blue">{{ referrer.referral_code }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Spillover notice -->
                    <div v-if="referrer.left_spillover?.is_spillover || referrer.right_spillover?.is_spillover" class="pls-spillover">
                        <svg class="pls-spillover-icon" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
                        <div>
                            <p class="pls-spillover-title">Spillover Placement</p>
                            <p class="pls-spillover-sub">Immediate positions are full. You'll be placed deeper in the tree automatically.</p>
                        </div>
                    </div>

                    <p class="pls-pos-heading">Select Your Position</p>

                    <!-- Position grid -->
                    <div class="pls-pos-grid">
                        <button @click="selectPosition('left')" :disabled="!referrer.left_available || isLoading" class="pls-pos-btn"
                            :class="selectedPosition === 'left' ? 'pls-pos-btn--left-on' : referrer.left_available ? 'pls-pos-btn--left' : 'pls-pos-btn--off'">
                            <div class="pls-pos-ico" :class="selectedPosition === 'left' ? 'pls-pos-ico--left-on' : referrer.left_available ? 'pls-pos-ico--idle' : 'pls-pos-ico--off'">
                                <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/></svg>
                            </div>
                            <p class="pls-pos-label">Left</p>
                            <p v-if="referrer.left_available && !referrer.left_spillover?.is_spillover" class="pls-pos-state pls-pos-state--ok">Direct</p>
                            <p v-else-if="referrer.left_available && referrer.left_spillover?.is_spillover" class="pls-pos-state pls-pos-state--spill">Spillover → {{ referrer.left_spillover.actual_parent_uid }}</p>
                            <p v-else class="pls-pos-state pls-pos-state--full">Tree Full</p>
                            <span v-if="selectedPosition === 'left'" class="pls-pos-check pls-pos-check--left">
                                <svg viewBox="0 0 12 12" fill="currentColor"><path d="M10 3L5 8.5 2 5.5" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
                            </span>
                        </button>

                        <button @click="selectPosition('right')" :disabled="!referrer.right_available || isLoading" class="pls-pos-btn"
                            :class="selectedPosition === 'right' ? 'pls-pos-btn--right-on' : referrer.right_available ? 'pls-pos-btn--right' : 'pls-pos-btn--off'">
                            <div class="pls-pos-ico" :class="selectedPosition === 'right' ? 'pls-pos-ico--right-on' : referrer.right_available ? 'pls-pos-ico--idle' : 'pls-pos-ico--off'">
                                <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
                            </div>
                            <p class="pls-pos-label">Right</p>
                            <p v-if="referrer.right_available && !referrer.right_spillover?.is_spillover" class="pls-pos-state pls-pos-state--ok">Direct</p>
                            <p v-else-if="referrer.right_available && referrer.right_spillover?.is_spillover" class="pls-pos-state pls-pos-state--spill-r">Spillover → {{ referrer.right_spillover.actual_parent_uid }}</p>
                            <p v-else class="pls-pos-state pls-pos-state--full">Tree Full</p>
                            <span v-if="selectedPosition === 'right'" class="pls-pos-check pls-pos-check--right">
                                <svg viewBox="0 0 12 12" fill="currentColor"><path d="M10 3L5 8.5 2 5.5" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
                            </span>
                        </button>
                    </div>

                    <!-- Confirm -->
                    <button @click="confirmPlacement" :disabled="!selectedPosition || isLoading" class="pls-confirm"
                        :class="selectedPosition === 'left' ? 'pls-confirm--left' : selectedPosition === 'right' ? 'pls-confirm--right' : 'pls-confirm--none'">
                        <svg v-if="isLoading" class="pls-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10" stroke-opacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"/></svg>
                        <svg v-else viewBox="0 0 20 20" fill="currentColor" style="width:17px;height:17px"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
                        {{ isLoading ? 'Processing…' : 'Confirm ' + (selectedPosition ? selectedPosition.charAt(0).toUpperCase() + selectedPosition.slice(1) : '') + ' Position' }}
                    </button>
                </template>

                <!-- ── Branch B: no referrer → search ── -->
                <template v-else>
                    <div class="pls-sec-hdr">
                        <div class="pls-sec-icon pls-sec-icon--purple">
                            <svg viewBox="0 0 20 20" fill="currentColor"><path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z"/></svg>
                        </div>
                        <div>
                            <h3 class="pls-sec-title">Enter Referrer Details</h3>
                            <p class="pls-sec-sub">Enter a referrer UID or Referral Code to continue</p>
                        </div>
                    </div>

                    <!-- Search row -->
                    <div class="pls-search-wrap">
                        <label class="pls-label">Referrer UID or Referral Code</label>
                        <div class="pls-search-row">
                            <input v-model="referrerIdentifier" type="text" placeholder="Enter UID or referral code"
                                class="pls-input" @keyup.enter="searchReferrer" :disabled="isLoading" />
                            <button @click="searchReferrer" :disabled="!referrerIdentifier.trim() || isLoading" class="pls-search-btn">
                                <svg v-if="isLoading" class="pls-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10" stroke-opacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"/></svg>
                                <svg v-else viewBox="0 0 20 20" fill="currentColor" style="width:16px;height:16px"><path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/></svg>
                                <span>{{ isLoading ? 'Searching…' : 'Search' }}</span>
                            </button>
                        </div>
                        <p class="pls-hint">
                            <svg viewBox="0 0 20 20" fill="currentColor" style="width:12px;height:12px;flex-shrink:0"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
                            You can enter either the UID or Referral Code of your sponsor
                        </p>
                    </div>

                    <!-- Found referrer -->
                    <div v-if="foundReferrer && !isLoading" class="anim-up" style="--d:0ms">
                        <div class="pls-found-card">
                            <div class="pls-ref-head">
                                <div class="pls-avatar pls-avatar--green">{{ (foundReferrer.display_name || foundReferrer.name)?.charAt(0).toUpperCase() || '?' }}</div>
                                <div class="pls-ref-info">
                                    <p class="pls-ref-name">{{ foundReferrer.display_name || foundReferrer.name }}</p>
                                    <p class="pls-ref-email">{{ foundReferrer.email }}</p>
                                </div>
                                <div class="pls-found-ok">
                                    <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
                                </div>
                            </div>
                            <div class="pls-meta-grid">
                                <div class="pls-meta-item">
                                    <span class="pls-meta-lbl">User ID</span>
                                    <span class="pls-meta-val">{{ foundReferrer.uid }}</span>
                                </div>
                                <div class="pls-meta-item">
                                    <span class="pls-meta-lbl">Referral Code</span>
                                    <span class="pls-meta-val pls-meta-val--green">{{ foundReferrer.referral_code }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Spillover notice -->
                        <div v-if="foundReferrer.left_spillover?.is_spillover || foundReferrer.right_spillover?.is_spillover" class="pls-spillover">
                            <svg class="pls-spillover-icon" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
                            <div>
                                <p class="pls-spillover-title">Spillover Placement</p>
                                <p class="pls-spillover-sub">Immediate positions are full. You'll be placed deeper in the tree automatically.</p>
                            </div>
                        </div>

                        <p class="pls-pos-heading">Select Your Position</p>

                        <div class="pls-pos-grid">
                            <button @click="selectPosition('left')" :disabled="!foundReferrer.left_available || isLoading" class="pls-pos-btn"
                                :class="selectedPosition === 'left' ? 'pls-pos-btn--left-on' : foundReferrer.left_available ? 'pls-pos-btn--left' : 'pls-pos-btn--off'">
                                <div class="pls-pos-ico" :class="selectedPosition === 'left' ? 'pls-pos-ico--left-on' : foundReferrer.left_available ? 'pls-pos-ico--idle' : 'pls-pos-ico--off'">
                                    <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/></svg>
                                </div>
                                <p class="pls-pos-label">Left</p>
                                <p v-if="foundReferrer.left_available && !foundReferrer.left_spillover?.is_spillover" class="pls-pos-state pls-pos-state--ok">Direct</p>
                                <p v-else-if="foundReferrer.left_available && foundReferrer.left_spillover?.is_spillover" class="pls-pos-state pls-pos-state--spill">Spillover → {{ foundReferrer.left_spillover.actual_parent_uid }}</p>
                                <p v-else class="pls-pos-state pls-pos-state--full">Tree Full</p>
                                <span v-if="selectedPosition === 'left'" class="pls-pos-check pls-pos-check--left">
                                    <svg viewBox="0 0 12 12" fill="none"><path d="M10 3L5 8.5 2 5.5" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                                </span>
                            </button>

                            <button @click="selectPosition('right')" :disabled="!foundReferrer.right_available || isLoading" class="pls-pos-btn"
                                :class="selectedPosition === 'right' ? 'pls-pos-btn--right-on' : foundReferrer.right_available ? 'pls-pos-btn--right' : 'pls-pos-btn--off'">
                                <div class="pls-pos-ico" :class="selectedPosition === 'right' ? 'pls-pos-ico--right-on' : foundReferrer.right_available ? 'pls-pos-ico--idle' : 'pls-pos-ico--off'">
                                    <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
                                </div>
                                <p class="pls-pos-label">Right</p>
                                <p v-if="foundReferrer.right_available && !foundReferrer.right_spillover?.is_spillover" class="pls-pos-state pls-pos-state--ok">Direct</p>
                                <p v-else-if="foundReferrer.right_available && foundReferrer.right_spillover?.is_spillover" class="pls-pos-state pls-pos-state--spill-r">Spillover → {{ foundReferrer.right_spillover.actual_parent_uid }}</p>
                                <p v-else class="pls-pos-state pls-pos-state--full">Tree Full</p>
                                <span v-if="selectedPosition === 'right'" class="pls-pos-check pls-pos-check--right">
                                    <svg viewBox="0 0 12 12" fill="none"><path d="M10 3L5 8.5 2 5.5" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                                </span>
                            </button>
                        </div>

                        <button @click="confirmPlacementWithFoundReferrer" :disabled="!selectedPosition || isLoading" class="pls-confirm"
                            :class="selectedPosition === 'left' ? 'pls-confirm--left' : selectedPosition === 'right' ? 'pls-confirm--right' : 'pls-confirm--none'">
                            <svg v-if="isLoading" class="pls-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10" stroke-opacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"/></svg>
                            <svg v-else viewBox="0 0 20 20" fill="currentColor" style="width:17px;height:17px"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
                            {{ isLoading ? 'Processing…' : 'Confirm ' + (selectedPosition ? selectedPosition.charAt(0).toUpperCase() + selectedPosition.slice(1) : '') + ' Position' }}
                        </button>
                    </div>
                </template>

                <!-- Info box -->
                <div class="pls-info">
                    <svg class="pls-info-icon" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
                    <div>
                        <p class="pls-info-title">Important Information</p>
                        <ul class="pls-info-list">
                            <li>Once set, your binary placement cannot be changed</li>
                            <li>Choose your position carefully based on your strategy</li>
                            <li>Full positions are handled via automatic spillover</li>
                            <li>You'll access your account immediately after confirmation</li>
                        </ul>
                    </div>
                </div>
            </div>

            <!-- Logout -->
            <div class="pls-logout-wrap">
                <button @click="logout" class="pls-logout-btn">
                    <svg viewBox="0 0 20 20" fill="currentColor" style="width:15px;height:15px"><path fill-rule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clip-rule="evenodd"/></svg>
                    Logout
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { router } from '@inertiajs/vue3'
import axios from 'axios'

const props = defineProps({
    user: Object,
    referrer: Object,
    binaryEnabled: Boolean
})

const selectedPosition = ref(null)
const referrerIdentifier = ref('')
const foundReferrer = ref(null)
const isLoading = ref(false)
const alert = ref({
    show: false,
    type: 'info',
    message: ''
})

const alertClass = computed(() => {
    const baseClasses = 'border rounded-xl'
    switch (alert.value.type) {
        case 'success':
            return `${baseClasses} bg-green-500/10 border-green-500/30 text-green-400`
        case 'error':
            return `${baseClasses} bg-red-500/10 border-red-500/30 text-red-400`
        case 'warning':
            return `${baseClasses} bg-yellow-500/10 border-yellow-500/30 text-yellow-400`
        default:
            return `${baseClasses} bg-blue-500/10 border-blue-500/30 text-blue-400`
    }
})

const alertIcon = computed(() => {
    switch (alert.value.type) {
        case 'success':
            return 'fas fa-check-circle'
        case 'error':
            return 'fas fa-exclamation-circle'
        case 'warning':
            return 'fas fa-exclamation-triangle'
        default:
            return 'fas fa-info-circle'
    }
})

function selectPosition(position) {
    selectedPosition.value = position
}

function showAlert(type, message) {
    alert.value = {
        show: true,
        type,
        message
    }
    setTimeout(() => {
        alert.value.show = false
    }, 5000)
}

async function searchReferrer() {
    if (!referrerIdentifier.value.trim()) {
        showAlert('warning', 'Please enter a referrer UID or Referral Code')
        return
    }

    isLoading.value = true
    selectedPosition.value = null

    try {
        const response = await axios.post('/user/placement/validate-referrer', {
            referrer_identifier: referrerIdentifier.value.trim()
        })

        if (response.data.success) {
            foundReferrer.value = response.data.referrer
            showAlert('success', response.data.message)
        } else {
            showAlert('error', response.data.message)
        }
    } catch (error) {
        const message = error.response?.data?.message || 'Failed to validate referrer. Please try again.'
        showAlert('error', message)
        foundReferrer.value = null
    } finally {
        isLoading.value = false
    }
}

async function confirmPlacement() {
    if (!selectedPosition.value || !props.referrer) {
        return
    }

    isLoading.value = true

    try {
        const response = await axios.post('/user/placement/set', {
            referrer_id: props.referrer.id,
            position: selectedPosition.value
        })

        if (response.data.success) {
            showAlert('success', response.data.message)
            setTimeout(() => {
                router.visit(response.data.redirect_url)
            }, 1500)
        } else {
            showAlert('error', response.data.message)
            isLoading.value = false
        }
    } catch (error) {
        const message = error.response?.data?.message || 'Failed to set placement. Please try again.'
        showAlert('error', message)
        isLoading.value = false
    }
}

async function confirmPlacementWithFoundReferrer() {
    if (!selectedPosition.value || !foundReferrer.value) {
        return
    }

    isLoading.value = true

    try {
        const response = await axios.post('/user/placement/set', {
            referrer_id: foundReferrer.value.id,
            position: selectedPosition.value
        })

        if (response.data.success) {
            showAlert('success', response.data.message)
            setTimeout(() => {
                router.visit(response.data.redirect_url)
            }, 1500)
        } else {
            showAlert('error', response.data.message)
            isLoading.value = false
        }
    } catch (error) {
        const message = error.response?.data?.message || 'Failed to set placement. Please try again.'
        showAlert('error', message)
        isLoading.value = false
    }
}

function logout() {
    router.post('/logout')
}
</script>

<style scoped>
/* ── Animation ── */
@keyframes slideUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: none; }
}
.anim-up {
    animation: slideUp 0.45s cubic-bezier(0.22,1,0.36,1) both;
    animation-delay: var(--d, 0ms);
}
@keyframes pls-blob-pulse {
    0%,100% { opacity: 0.45; transform: scale(1); }
    50%      { opacity: 0.75; transform: scale(1.06); }
}
@keyframes pls-spin-kf { to { transform: rotate(360deg); } }
.pls-spin {
    width: 17px; height: 17px;
    animation: pls-spin-kf 0.7s linear infinite;
    flex-shrink: 0;
}

/* ── Root ── */
.pls-root {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.25rem 1rem;
    background: linear-gradient(135deg, #0a0416 0%, #140828 50%, #0a0416 100%);
    position: relative;
    overflow: hidden;
}

/* ── Background grid ── */
.pls-grid-bg {
    position: absolute; inset: 0;
    background-image:
        linear-gradient(to right,  rgba(167,93,237,0.07) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(167,93,237,0.07) 1px, transparent 1px);
    background-size: 38px 38px;
}

/* ── Blobs ── */
.pls-blobs { position: absolute; inset: 0; overflow: hidden; pointer-events: none; }
.pls-blob  { position: absolute; border-radius: 50%; filter: blur(72px); animation: pls-blob-pulse 5s ease-in-out infinite; }
.pls-blob--blue   { top: -6rem; left: -6rem; width: 26rem; height: 26rem; background: rgba(59,130,246,0.11); }
.pls-blob--purple { bottom: -5rem; right: -5rem; width: 22rem; height: 22rem; background: rgba(167,93,237,0.14); animation-delay: 2.5s; }

/* ── Content wrap ── */
.pls-wrap {
    position: relative; z-index: 10;
    width: 100%; max-width: 600px;
    margin: 0 auto;
}

/* ── Header ── */
.pls-hdr { text-align: center; margin-bottom: 1.25rem; }
.pls-hdr-icon {
    display: inline-flex; align-items: center; justify-content: center;
    width: 68px; height: 68px;
    background: linear-gradient(135deg, rgba(59,130,246,0.25), rgba(167,93,237,0.35));
    border: 1px solid rgba(167,93,237,0.3);
    border-radius: 50%;
    margin-bottom: 0.875rem;
    color: #fff;
    box-shadow: 0 0 28px rgba(167,93,237,0.22);
}
.pls-hdr-icon svg { width: 30px; height: 30px; }
.pls-hdr-title {
    font-size: clamp(1.4rem, 5vw, 2.2rem);
    font-weight: 900;
    color: #fff;
    letter-spacing: -0.02em;
    margin-bottom: 0.45rem;
    line-height: 1.15;
}
.pls-hdr-sub {
    font-size: clamp(0.8rem, 2.5vw, 0.9rem);
    color: rgba(203,195,227,0.65);
    max-width: 400px;
    margin: 0 auto;
    line-height: 1.5;
}

/* ── Alert ── */
.pls-alert {
    display: flex; align-items: flex-start; gap: 0.625rem;
    padding: 0.75rem 0.875rem;
    border-radius: 12px; border: 1px solid;
    margin-bottom: 0.875rem;
    backdrop-filter: blur(12px);
}
.pls-alert--success { background: rgba(34,197,94,0.09);  border-color: rgba(34,197,94,0.28);  color: #4ade80; }
.pls-alert--error   { background: rgba(239,68,68,0.09);  border-color: rgba(239,68,68,0.28);  color: #f87171; }
.pls-alert--warning { background: rgba(251,191,36,0.09); border-color: rgba(251,191,36,0.28); color: #fbbf24; }
.pls-alert--info    { background: rgba(59,130,246,0.09); border-color: rgba(59,130,246,0.28); color: #60a5fa; }
.pls-alert-icon { flex-shrink: 0; width: 18px; height: 18px; margin-top: 1px; }
.pls-alert-icon svg { width: 18px; height: 18px; }
.pls-alert-msg { flex: 1; font-size: 0.85rem; font-weight: 500; line-height: 1.4; }
.pls-alert-close {
    flex-shrink: 0; display: flex; align-items: center; justify-content: center;
    width: 26px; height: 26px; background: none; border: none;
    cursor: pointer; color: currentColor; opacity: 0.65; border-radius: 6px; transition: opacity 0.2s;
}
.pls-alert-close:hover { opacity: 1; }
.pls-alert-close svg { width: 14px; height: 14px; }

/* ── Main card ── */
.pls-card {
    background: rgba(20,10,40,0.93);
    border: 1px solid rgba(167,93,237,0.16);
    border-radius: 20px;
    padding: 1.375rem;
    backdrop-filter: blur(24px);
    box-shadow: 0 8px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(167,93,237,0.07);
    position: relative; overflow: hidden;
}
.pls-card::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(167,93,237,0.3), transparent);
}

/* ── Section header ── */
.pls-sec-hdr { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1.125rem; }
.pls-sec-icon {
    flex-shrink: 0; width: 42px; height: 42px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center; color: #fff;
}
.pls-sec-icon svg { width: 18px; height: 18px; }
.pls-sec-icon--green  { background: linear-gradient(135deg,#22c55e,#10b981); box-shadow: 0 3px 10px rgba(34,197,94,0.28); }
.pls-sec-icon--purple { background: linear-gradient(135deg,#a75ded,#7c3aed); box-shadow: 0 3px 10px rgba(167,93,237,0.28); }
.pls-sec-title { font-size: 1rem; font-weight: 700; color: #fff; margin-bottom: 0.15rem; }
.pls-sec-sub   { font-size: 0.775rem; color: rgba(203,195,227,0.55); }

/* ── Avatars ── */
.pls-avatar {
    flex-shrink: 0; width: 52px; height: 52px; border-radius: 50%;
    background: linear-gradient(135deg, rgba(59,130,246,0.55), rgba(167,93,237,0.75));
    display: flex; align-items: center; justify-content: center;
    font-size: 1.2rem; font-weight: 700; color: #fff;
    box-shadow: 0 3px 10px rgba(167,93,237,0.25);
}
.pls-avatar--green { background: linear-gradient(135deg,#22c55e,#10b981); box-shadow: 0 3px 10px rgba(34,197,94,0.25); }

/* ── Referrer card ── */
.pls-ref-card {
    background: rgba(167,93,237,0.06); border: 1px solid rgba(167,93,237,0.14);
    border-radius: 14px; padding: 1.125rem; margin-bottom: 1.125rem;
}
.pls-ref-head { display: flex; align-items: center; gap: 0.875rem; margin-bottom: 0.875rem; }
.pls-ref-info { flex: 1; min-width: 0; }
.pls-ref-name  { font-size: 0.975rem; font-weight: 700; color: #fff; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pls-ref-email { font-size: 0.775rem; color: rgba(203,195,227,0.55); margin-top: 0.1rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* ── Found referrer card ── */
.pls-found-card {
    background: rgba(34,197,94,0.07); border: 1px solid rgba(34,197,94,0.2);
    border-radius: 14px; padding: 1.125rem; margin-bottom: 1.125rem;
}
.pls-found-ok { flex-shrink: 0; color: #4ade80; width: 30px; height: 30px; }
.pls-found-ok svg { width: 30px; height: 30px; }

/* ── Meta grid ── */
.pls-meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; }
.pls-meta-item { background: rgba(10,4,22,0.45); border-radius: 10px; padding: 0.55rem 0.75rem; }
.pls-meta-lbl  { display: block; font-size: 0.68rem; color: rgba(203,195,227,0.45); margin-bottom: 0.2rem; text-transform: uppercase; letter-spacing: 0.04em; }
.pls-meta-val  { display: block; font-size: 0.875rem; font-weight: 700; color: #fff; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pls-meta-val--blue  { color: #60a5fa; }
.pls-meta-val--green { color: #4ade80; }

/* ── Spillover notice ── */
.pls-spillover {
    display: flex; gap: 0.625rem; align-items: flex-start;
    background: rgba(59,130,246,0.08); border: 1px solid rgba(59,130,246,0.22);
    border-radius: 10px; padding: 0.75rem; margin-bottom: 1rem;
}
.pls-spillover-icon { flex-shrink: 0; width: 17px; height: 17px; color: #60a5fa; margin-top: 1px; }
.pls-spillover-title { font-size: 0.775rem; font-weight: 700; color: #fff; margin-bottom: 0.2rem; }
.pls-spillover-sub   { font-size: 0.72rem; color: rgba(203,195,227,0.6); line-height: 1.45; }

/* ── Position heading ── */
.pls-pos-heading { font-size: 0.9rem; font-weight: 700; color: #fff; margin-bottom: 0.75rem; }

/* ── Position grid ── */
.pls-pos-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; margin-bottom: 1.125rem; }

/* ── Position button ── */
.pls-pos-btn {
    position: relative; display: flex; flex-direction: column;
    align-items: center; text-align: center;
    padding: 1.125rem 0.875rem; border-radius: 14px; border: 2px solid;
    min-height: 148px; cursor: pointer; background: none;
    transition: all 0.22s cubic-bezier(0.22,1,0.36,1);
}
.pls-pos-btn--left-on   { border-color: rgba(59,130,246,0.85);  background: rgba(59,130,246,0.11);  box-shadow: 0 4px 18px rgba(59,130,246,0.18);  transform: scale(1.02); }
.pls-pos-btn--left      { border-color: rgba(100,116,139,0.35); background: rgba(167,93,237,0.04); }
.pls-pos-btn--left:hover { border-color: rgba(59,130,246,0.5); background: rgba(59,130,246,0.08); transform: scale(1.02); }
.pls-pos-btn--right-on  { border-color: rgba(167,93,237,0.85); background: rgba(167,93,237,0.11); box-shadow: 0 4px 18px rgba(167,93,237,0.18); transform: scale(1.02); }
.pls-pos-btn--right     { border-color: rgba(100,116,139,0.35); background: rgba(167,93,237,0.04); }
.pls-pos-btn--right:hover { border-color: rgba(167,93,237,0.5); background: rgba(167,93,237,0.08); transform: scale(1.02); }
.pls-pos-btn--off       { border-color: rgba(100,116,139,0.18); background: rgba(30,20,50,0.28); opacity: 0.42; cursor: not-allowed; }

/* ── Position icon circle ── */
.pls-pos-ico {
    width: 50px; height: 50px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 0.625rem; transition: all 0.22s; color: #fff;
}
.pls-pos-ico svg { width: 22px; height: 22px; }
.pls-pos-ico--idle      { background: rgba(167,93,237,0.14); }
.pls-pos-ico--left-on   { background: #3b82f6; box-shadow: 0 4px 14px rgba(59,130,246,0.38); }
.pls-pos-ico--right-on  { background: #a75ded; box-shadow: 0 4px 14px rgba(167,93,237,0.38); }
.pls-pos-ico--off       { background: rgba(30,20,50,0.5); }

.pls-pos-label { font-size: 0.95rem; font-weight: 700; color: #fff; margin-bottom: 0.3rem; }

/* ── Position state text ── */
.pls-pos-state { font-size: 0.72rem; line-height: 1.35; }
.pls-pos-state--ok     { color: #4ade80; }
.pls-pos-state--spill  { color: #60a5fa; }
.pls-pos-state--spill-r{ color: #c084fc; }
.pls-pos-state--full   { color: #f87171; }

/* ── Position check badge ── */
.pls-pos-check {
    position: absolute; top: 0.55rem; right: 0.55rem;
    width: 20px; height: 20px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
}
.pls-pos-check--left  { background: #3b82f6; box-shadow: 0 2px 8px rgba(59,130,246,0.35); }
.pls-pos-check--right { background: #a75ded; box-shadow: 0 2px 8px rgba(167,93,237,0.35); }

/* ── Confirm button ── */
.pls-confirm {
    width: 100%; display: flex; align-items: center; justify-content: center; gap: 0.5rem;
    padding: 0.825rem 1.5rem; border-radius: 12px; border: none;
    font-size: 0.9rem; font-weight: 700; color: #fff; cursor: pointer; min-height: 50px;
    transition: all 0.22s cubic-bezier(0.22,1,0.36,1);
}
.pls-confirm:disabled { opacity: 0.45; cursor: not-allowed; }
.pls-confirm--left  { background: linear-gradient(135deg,#3b82f6,#2563eb); box-shadow: 0 4px 18px rgba(59,130,246,0.3); }
.pls-confirm--left:not(:disabled):hover  { filter: brightness(1.1); box-shadow: 0 6px 24px rgba(59,130,246,0.4); }
.pls-confirm--right { background: linear-gradient(135deg,#a75ded,#7c3aed); box-shadow: 0 4px 18px rgba(167,93,237,0.3); }
.pls-confirm--right:not(:disabled):hover { filter: brightness(1.1); box-shadow: 0 6px 24px rgba(167,93,237,0.4); }
.pls-confirm--none  { background: rgba(60,40,90,0.45); }

/* ── Search ── */
.pls-search-wrap { margin-bottom: 1.125rem; }
.pls-label { display: block; font-size: 0.72rem; font-weight: 600; color: rgba(203,195,227,0.7); margin-bottom: 0.5rem; text-transform: uppercase; letter-spacing: 0.05em; }
.pls-search-row { display: flex; gap: 0.5rem; }
.pls-input {
    flex: 1; padding: 0.7rem 0.875rem;
    background: rgba(167,93,237,0.06); border: 1px solid rgba(167,93,237,0.18);
    border-radius: 10px; font-size: 0.875rem; color: #fff; min-height: 46px;
    outline: none; transition: border-color 0.2s, box-shadow 0.2s;
}
.pls-input::placeholder { color: rgba(203,195,227,0.3); }
.pls-input:focus { border-color: rgba(167,93,237,0.5); box-shadow: 0 0 0 3px rgba(167,93,237,0.1); }
.pls-input:disabled { opacity: 0.55; cursor: not-allowed; }
.pls-search-btn {
    display: flex; align-items: center; gap: 0.4rem; flex-shrink: 0;
    padding: 0 1.125rem; min-height: 46px; min-width: 96px;
    background: linear-gradient(135deg,#3b82f6,#a75ded); border: none;
    border-radius: 10px; color: #fff; font-size: 0.85rem; font-weight: 700;
    cursor: pointer; white-space: nowrap; transition: all 0.22s; justify-content: center;
}
.pls-search-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.pls-search-btn:not(:disabled):hover { filter: brightness(1.1); box-shadow: 0 4px 14px rgba(167,93,237,0.28); }
.pls-hint { display: flex; align-items: center; gap: 0.35rem; font-size: 0.72rem; color: rgba(203,195,227,0.45); margin-top: 0.45rem; line-height: 1.4; }

/* ── Info box ── */
.pls-info {
    display: flex; gap: 0.625rem; align-items: flex-start;
    background: rgba(167,93,237,0.06); border: 1px solid rgba(167,93,237,0.16);
    border-radius: 12px; padding: 0.875rem; margin-top: 1.125rem;
}
.pls-info-icon { flex-shrink: 0; width: 18px; height: 18px; color: #a75ded; margin-top: 1px; }
.pls-info-title { font-size: 0.775rem; font-weight: 700; color: rgba(203,195,227,0.85); margin-bottom: 0.4rem; }
.pls-info-list { font-size: 0.72rem; color: rgba(203,195,227,0.6); line-height: 1.55; list-style: disc; padding-left: 1rem; }
.pls-info-list li + li { margin-top: 0.2rem; }

/* ── Logout ── */
.pls-logout-wrap { text-align: center; margin-top: 1rem; }
.pls-logout-btn {
    display: inline-flex; align-items: center; gap: 0.45rem;
    padding: 0.55rem 1.125rem; background: none; border: none;
    color: rgba(203,195,227,0.45); font-size: 0.825rem; font-weight: 500;
    cursor: pointer; border-radius: 8px; transition: color 0.2s;
}
.pls-logout-btn:hover { color: rgba(203,195,227,0.85); }

/* ── Responsive ── */
@media (max-width: 420px) {
    .pls-root { padding: 1rem 0.75rem; }
    .pls-card { padding: 1.125rem; border-radius: 16px; }
    .pls-pos-grid { gap: 0.5rem; }
    .pls-pos-btn { min-height: 130px; padding: 0.875rem 0.625rem; }
    .pls-pos-ico { width: 44px; height: 44px; }
    .pls-pos-ico svg { width: 19px; height: 19px; }
    .pls-search-row { flex-direction: column; }
    .pls-search-btn { width: 100%; }
    .pls-meta-grid { grid-template-columns: 1fr; }
    .pls-hdr-icon { width: 60px; height: 60px; }
    .pls-hdr-icon svg { width: 26px; height: 26px; }
}
@media (min-width: 640px) {
    .pls-card { padding: 1.75rem; }
    .pls-pos-btn { min-height: 160px; padding: 1.375rem 1rem; }
    .pls-pos-ico { width: 58px; height: 58px; }
    .pls-pos-ico svg { width: 25px; height: 25px; }
    .pls-pos-label { font-size: 1rem; }
    .pls-avatar { width: 58px; height: 58px; font-size: 1.3rem; }
}
</style>
